import React, { useState, useEffect } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import { Box, Paper, Typography, Button, Chip, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';

const MissingRate = () => {
  const [step, setStep] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [aiReport, setAiReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  useEffect(() => {
    console.log('🔍 State changed - Step:', step, 'selectedDatasetId:', selectedDatasetId, 'selectedDataset:', selectedDataset);
  }, [step, selectedDatasetId, selectedDataset]);

  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    } else {
      fetchDatasets();
    }
  }, []);

  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        const datasetId = analysis.input_parameters.dataset_id;
        setSelectedDatasetId(datasetId);
        setSelectedDataset({
          id: datasetId,
          name: analysis.input_parameters.dataset_name,
          rows: analysis.input_parameters.rows,
          columns: analysis.input_parameters.columns
        });
      }
      
      if (analysis.output_result) {
        setResults(analysis.output_result);
        if (analysis.input_parameters?.ai_report) setAiReport(analysis.input_parameters.ai_report);        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Please run the analysis to see results.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
      fetchDatasets();
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      console.log('📦 Fetched datasets:', res.data);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        size_mb: (ds.file_size / 1e6).toFixed(2),
        rows: ds.row_count,
        columns: ds.column_count,
        exists: true
      }));
      setDatasets(datasets);
    } catch (err) {
      toast.error('Failed to load datasets');
    }
  };

  const handleSelectDataset = async () => {
    console.log('🖱️ handleSelectDataset called, selectedDatasetId:', selectedDatasetId);
    if (!selectedDatasetId) return;
    setLoading(true);
    try {
      const res = await api.post('/api/missing-rate/select-dataset', { dataset_id: parseInt(selectedDatasetId) });
      console.log('📥 Select dataset response:', res.data);
      const selectedDs = datasets.find(ds => ds.id == selectedDatasetId);
      setSelectedDataset({
        id: res.data.table_id,
        name: selectedDs?.name || 'Unknown',
        rows: res.data.rows,
        columns: res.data.columns
      });
      setResults(null);
      setAiReport(null);
      setStep(1);
      toast.success(`Loaded: ${res.data.rows?.toLocaleString()} rows`);
    } catch (err) {
      console.error('❌ Select dataset error:', err);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    console.log('🔬 handleAnalyze called, selectedDataset:', selectedDataset);
    setAnalyzing(true);
    try {
      const res = await api.post('/api/missing-rate/analyze');
      setResults(res.data.results);
      setAiReport(null);
      setStep(2);
      toast.success(res.data.message);
    } catch {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/api/missing-rate/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'missing_rate.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleAIReport = async () => {
    if (!results) {
      toast.error('Run analysis first');
      return;
    }
    setGeneratingReport(true);
    setAiReport(null);
    try {
      const res = await api.post('/api/missing-rate/ai-report');
      setAiReport(res.data.report);
      toast.success('AI report generated');
    } catch (err) {
      toast.error('Failed to generate report. Is Ollama running?');
    } finally {
      setGeneratingReport(false);
    }
  };
    
  const handleSaveAnalysis = async () => {
    console.log('💾 handleSaveAnalysis called, selectedDataset:', selectedDataset);
    if (!selectedDataset) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    if (!results) {
      toast.error('No results to save. Please run the analysis first.');
      return;
    }
    
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'missing_rate',
        description: analysisDescription,
        input_parameters: {
          dataset_id: selectedDataset.id,
          dataset_name: selectedDataset.name,
          rows: selectedDataset.rows,
          ai_report: aiReport,
          columns: selectedDataset.columns
        },
        output_result: { ...results, ai_report: aiReport },
        status: analysisStatus,
        comments: comments
      };
      console.log('📤 Saving payload:', payload);
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    }
  };

  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, { 
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const stepTabs = ['Select', 'Review', 'Results'];
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };

  if (isLoadingAnalysis) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading analysis...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Missing Rate Analysis</h1>
      
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
        {stepTabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (idx === 0) setStep(0);
              else if (idx === 1 && selectedDataset) setStep(1);
              else if (idx === 2 && results) setStep(2);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              background: step === idx ? '#1abc9c' : 'transparent',
              color: step === idx ? 'white' : '#7f8c8d',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Step 0: Select Dataset */}
      {step === 0 && (
        <div style={gs}>
          <div style={ghs}>📊 Select Dataset</div>
          <div style={gcs}>
            {selectedDataset && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                background: '#e8f8f5', 
                borderRadius: '8px',
                borderLeft: '4px solid #1abc9c'
              }}>
                <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                  📌 Currently selected dataset:
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                  {selectedDataset.name}
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                  {selectedDataset.rows?.toLocaleString()} rows • {selectedDataset.columns?.length || 0} columns
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <select
                value={selectedDatasetId}
                onChange={(e) => {
                  console.log('📋 Dropdown changed to:', e.target.value);
                  setSelectedDatasetId(e.target.value);
                  setSelectedDataset(null);
                }}
                disabled={isViewMode}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}
              >
                <option value="">-- Select a dataset --</option>
                {datasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name} ({ds.rows?.toLocaleString()} rows)
                  </option>
                ))}
              </select>
              <button
                onClick={handleSelectDataset}
                disabled={!selectedDatasetId || loading || isViewMode}
                style={{
                  padding: '12px 24px',
                  background: loading || isViewMode ? '#95a5a6' : '#1abc9c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || isViewMode ? 'not-allowed' : 'pointer',
                  fontSize: '13px'
                }}
              >
                {loading ? 'Loading...' : 'Load Dataset'}
              </button>
            </div>
            
            {selectedDataset && !results && (
              <div style={{ 
                marginTop: '12px', 
                fontSize: '12px', 
                color: '#f39c12',
                textAlign: 'center'
              }}>
                ⚠️ Dataset loaded. Click "Review" tab to run analysis.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 1: Review Dataset & Run Analysis */}
      {step === 1 && selectedDataset && (
        <div>
          <div style={gs}>
            <div style={ghs}>📋 Dataset Summary</div>
            <div style={gcs}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Chip label={`${selectedDataset.rows?.toLocaleString()} rows`} sx={{ background: '#ecf0f1' }} />
                <Chip label={`${selectedDataset.columns?.length || 0} columns`} sx={{ background: '#ecf0f1' }} />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              onClick={() => {
                console.log('⬅️ Going back to Step 0, selectedDatasetId is:', selectedDatasetId);
                setStep(0);
              }}
              disabled={isViewMode}
              style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              ← Back
            </button>
            <button
              onClick={handleAnalyze}
              disabled={analyzing || isViewMode}
              style={{
                padding: '10px 24px',
                background: analyzing || isViewMode ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: analyzing || isViewMode ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              {analyzing ? '🔍 Analyzing...' : '🔍 Analyze Missing Rate'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Results */}
      {step === 2 && results && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
            {[
              { label: 'Total Rows', value: results.total_rows?.toLocaleString(), color: '#3498db' },
              { label: 'Total Columns', value: results.total_columns, color: '#2ecc71' },
              { label: 'Cols with Missing', value: results.columns_with_missing, color: '#f39c12' },
              { label: 'Missing Cells', value: results.total_missing_cells?.toLocaleString(), color: '#e74c3c' },
            ].map((card, i) => (
              <Paper key={i} sx={{ p: 2, textAlign: 'center', borderLeft: `4px solid ${card.color}`, borderRadius: '8px' }}>
                <Typography variant="h5" fontWeight={600} sx={{ color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="textSecondary">{card.label}</Typography>
              </Paper>
            ))}
          </div>

          {/* Results Table */}
          <div style={gs}>
            <div style={ghs}>📋 Missing Values by Column</div>
            <div style={{ ...gcs, padding: 0 }}>
              <div style={{ display: 'flex', background: '#2c3e50', color: 'white', fontWeight: 600, fontSize: '12px', padding: '10px 14px', borderBottom: '2px solid #1abc9c', position: 'sticky', top: 0 }}>
                <div style={{ width: '30%', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Column</div>
                <div style={{ width: '12%', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Missing</div>
                <div style={{ width: '35%', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Missing %</div>
                <div style={{ width: '10%', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.2)' }}>%</div>
                <div style={{ width: '13%', textAlign: 'center' }}>Non-Missing</div>
              </div>
              <div style={{ maxHeight: 500, overflow: 'auto' }}>
                {results.data?.map((row, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: '12px', borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <div style={{ width: '30%', padding: '10px 14px', fontFamily: 'monospace', borderRight: '1px solid #e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.Column}</div>
                    <div style={{ width: '12%', padding: '10px 14px', textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>{row['Missing Count'].toLocaleString()}</div>
                    <div style={{ width: '35%', padding: '10px 14px', borderRight: '1px solid #e0e0e0' }}>
                      <div style={{ height: 12, background: '#eee', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(row['Missing %'], 100)}%`, background: row['Missing %'] > 50 ? '#e74c3c' : row['Missing %'] > 20 ? '#f39c12' : '#3498db', borderRadius: 6, minWidth: row['Missing %'] > 0 ? 4 : 0 }} />
                      </div>
                    </div>
                    <div style={{ width: '10%', padding: '10px 14px', textAlign: 'center', fontWeight: 600, borderRight: '1px solid #e0e0e0', color: row['Missing %'] > 50 ? '#e74c3c' : row['Missing %'] > 20 ? '#f39c12' : '#27ae60' }}>{row['Missing %']}%</div>
                    <div style={{ width: '13%', padding: '10px 14px', textAlign: 'center' }}>{row['Non-Missing Count'].toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div style={{ ...gs, marginTop: '20px' }}>
            <div 
              style={{ 
                ...ghs, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={gcs}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #1abc9c'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or question..."
                    style={{
                      flex: 1,
                      padding: '10px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      resize: 'vertical',
                      minHeight: '60px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                    style={{
                      padding: '8px 20px',
                      background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      height: 'fit-content'
                    }}
                  >
                    {savingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
                
                {!savedAnalysisId && comments.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    💡 Comments will be permanently saved when you click "Save Analysis"
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* Action Area */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                  disabled={isViewMode}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this analysis..."
              disabled={isViewMode}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                disabled={isViewMode}
                size="small"
              >
                ← Back
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => { setStep(0); setSelectedDataset(null); setResults(null); setAnalysisStatus('draft'); setAnalysisDescription(''); setSavedAnalysisId(null); setComments([]); setAiReport(null); }}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              {isEditMode && savedAnalysisId && (
                <Button
                  variant="outlined"
                  onClick={handleUpdateMetadata}
                  disabled={updatingMetadata}
                  size="small"
                >
                  {updatingMetadata ? 'Updating...' : 'Update Metadata'}
                </Button>
              )}
              
              <Button
                variant="contained"
                onClick={handleExport}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              <Button
                variant="contained"
                onClick={handleAIReport}
                disabled={!results || generatingReport}
                size="small"
                sx={{ bgcolor: results ? '#8e44ad' : '#95a5a6', '&:hover': { bgcolor: '#7d3c98' } }}
              >
                🤖 {generatingReport ? 'Generating...' : 'AI Report'}
              </Button>

              {isEditMode && (
                <Button
                  variant="contained"
                  onClick={handleSaveAnalysis}
                  disabled={!results}
                  size="small"
                  sx={{ bgcolor: results ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
                >
                  💾 Save Analysis
                </Button>
              )}
            </Box>
          </div>

          {/* AI Report */}
          {aiReport && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: '#f8f4ff', borderRadius: 2, border: '1px solid #d5c8e8' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#6c3483' }}>
                🤖 AI-Generated Report
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {aiReport}
              </Typography>
            </Paper>
          )}
        </div>
      )}
    </div>
  );
};

export default MissingRate;